/**
 * Унифицированный компонент пустого состояния
 * Дата: 30.01.2026
 * 
 * Используется в:
 * - Users.tsx — "Пользователи не найдены"
 * - Templates.tsx — "Шаблоны не найдены"
 * - Services.tsx — "Услуги не найдены"
 * - AutoReplies.tsx — "Правила не найдены"
 * - Messages.tsx — "Сообщения не найдены"
 * - PhoneBinding.tsx — "Пациенты не найдены"
 * 
 * Связан с: components/ui/index.ts
 */
import { cn } from '@/utils/cn';
import { Inbox, Search, FileX, Users, MessageSquare, Settings } from 'lucide-react';
import { Button } from './Button';

export type EmptyStateType = 'default' | 'search' | 'noData' | 'noUsers' | 'noMessages' | 'noSettings';

export interface EmptyStateProps {
  /** Тип пустого состояния (определяет иконку) */
  type?: EmptyStateType;
  /** Заголовок */
  title?: string;
  /** Описание */
  description?: string;
  /** Кастомная иконка */
  icon?: React.ReactNode;
  /** Текст кнопки действия */
  actionText?: string;
  /** Callback при клике на кнопку */
  onAction?: () => void;
  /** Размер: sm, md, lg */
  size?: 'sm' | 'md' | 'lg';
  /** Дополнительные классы */
  className?: string;
}

const ICON_MAP: Record<EmptyStateType, React.ReactNode> = {
  default: <Inbox className="w-12 h-12 text-shell-muted/60" />,
  search: <Search className="w-12 h-12 text-shell-muted/60" />,
  noData: <FileX className="w-12 h-12 text-shell-muted/60" />,
  noUsers: <Users className="w-12 h-12 text-shell-muted/60" />,
  noMessages: <MessageSquare className="w-12 h-12 text-shell-muted/60" />,
  noSettings: <Settings className="w-12 h-12 text-shell-muted/60" />,
};

const DEFAULT_TITLES: Record<EmptyStateType, string> = {
  default: 'Нет данных',
  search: 'Ничего не найдено',
  noData: 'Данные отсутствуют',
  noUsers: 'Пользователи не найдены',
  noMessages: 'Сообщений нет',
  noSettings: 'Настройки не заданы',
};

/**
 * EmptyState — унифицированный компонент пустого состояния
 * 
 * @example
 * // Базовое использование
 * <EmptyState type="search" />
 * 
 * @example
 * // С кастомным текстом и действием
 * <EmptyState
 *   type="noUsers"
 *   title="Пользователи не найдены"
 *   description="Создайте первого пользователя"
 *   actionText="Создать"
 *   onAction={() => setIsCreateModalOpen(true)}
 * />
 */
export function EmptyState({
  type = 'default',
  title,
  description,
  icon,
  actionText,
  onAction,
  size = 'md',
  className,
}: EmptyStateProps) {
  const sizeClasses = {
    sm: 'py-4',
    md: 'py-8',
    lg: 'py-12',
  };

  const iconSizeClasses = {
    sm: '[&_svg]:w-8 [&_svg]:h-8',
    md: '[&_svg]:w-12 [&_svg]:h-12',
    lg: '[&_svg]:w-16 [&_svg]:h-16',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  return (
    <div 
      className={cn(
        'flex flex-col items-center justify-center text-center',
        sizeClasses[size],
        className
      )}
    >
      <div className={iconSizeClasses[size]}>
        {icon || ICON_MAP[type]}
      </div>
      
      <h3 className={cn(
        'mt-4 font-semibold text-shell-text',
        textSizeClasses[size]
      )}>
        {title || DEFAULT_TITLES[type]}
      </h3>
      
      {description && (
        <p className="mt-1 text-sm text-shell-muted max-w-sm">
          {description}
        </p>
      )}
      
      {actionText && onAction && (
        <Button 
          onClick={onAction}
          className="mt-4"
          size={size === 'sm' ? 'sm' : 'md'}
        >
          {actionText}
        </Button>
      )}
    </div>
  );
}
